/**
 * PlanSummary — the money header for a plan.
 *
 * TOTAL BUDGET is the hero on the left; the working numbers sit right-aligned
 * beside it with MAX BID in brand orange, because max bid is the one number you
 * actually read out loud at the table.
 *
 * 🔴 PLANNED AND TARGETED ARE BOTH SHOWN, AND THE GAP BETWEEN THEM IS THE
 * POINT. PLANNED is the sum of your earmarks — what you have set aside per
 * slot. TARGETED is what the primaries you actually listed would cost. A plan
 * that earmarks $60 at RB1 and lists a $51 target is $9 of deliberate room, and
 * a single "allocated" number cannot say that. They are labelled rather than
 * left to inference for exactly that reason.
 *
 * 🔴 SECURED SPLITS TARGETED, IT DOES NOT ADD TO IT. Once you have locked
 * players, TARGETED shows only what the names you are still shopping for would
 * cost and SECURED shows what you have already bought — two lines whose sum is
 * the number TARGETED showed on its own before, so no total moves and the
 * allocation bar, the alert thresholds and LEFT are all untouched. With nothing
 * locked the SECURED tile is not rendered at all and this panel is exactly what
 * it was.
 *
 * The allocation bar is DIRECT-LABELLED. Every segment gets a "POS $N" entry in
 * the legend written in text tokens, never in the segment's own color — a
 * color-only legend fails for anyone who cannot separate the RB green from the
 * WR blue, and this bar can carry six positions at once.
 */

import React, { useState } from "react";
import { theme } from "../../data/theme";
import { formatPpgTotal } from "./ppgTotals";
import { useIsNarrow, PHONE_BREAKPOINT } from "./useViewport";

const money = (n) => `$${Math.round(n)}`;

/**
 * 🔴 COMPACT IS A TILE, NOT A SMALLER Stat. On a phone these four sit in a 2x2
 * grid, so they left-align inside their own box and the SUB LINE IS DROPPED —
 * "leaves your earmarks and $1 for every unplanned spot" is four wrapped lines
 * at 190px and would triple the height of the block it annotates. It is not
 * deleted: it joins the tooltip ahead of the long explanation, on the middle
 * dot this page separates with everywhere else, so the same words are still
 * reachable by a long press and nothing has to be rewritten.
 */
function Stat({ label, value, accent, sub, title, compact = false }) {
  const tip = compact ? [sub, title].filter(Boolean).join(" · ") : title;
  if (compact) {
    return (
      <div title={tip} style={{
        padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
        borderRadius: theme.radius.md,
        background: theme.colors.bg.deep,
        border: `1px solid ${theme.colors.border.subtle}`,
        minWidth: 0,
      }}>
        <div style={{
          fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.heavy,
          letterSpacing: 0.7,
          color: theme.colors.text.faint,
          textTransform: "uppercase",
        }}>{label}</div>
        <div style={{
          fontFamily: theme.fonts.display,
          fontSize: theme.fontSize.title,
          fontWeight: theme.fontWeight.heavy,
          color: accent || theme.colors.text.primary,
          lineHeight: "24px",
        }}>{value}</div>
      </div>
    );
  }
  return (
    <div style={{ textAlign: "right" }} title={title}>
      <div style={{
        fontSize: theme.fontSize.caption,
        fontWeight: theme.fontWeight.heavy,
        letterSpacing: 0.7,
        color: theme.colors.text.faint,
        textTransform: "uppercase",
      }}>{label}</div>
      <div style={{
        fontFamily: theme.fonts.display,
        fontSize: theme.fontSize.title,
        fontWeight: theme.fontWeight.heavy,
        color: accent || theme.colors.text.primary,
        lineHeight: "22px",
      }}>{value}</div>
      {sub && (
        <div style={{
          fontSize: theme.fontSize.caption,
          color: theme.colors.text.faint,
          maxWidth: 148,
          lineHeight: 1.25,
          marginTop: 1,
        }}>{sub}</div>
      )}
    </div>
  );
}

/**
 * The one alert line, first match wins. Ordered by how much it costs the user
 * to not notice: over budget and cannot-fill are both plan-breaking, dead money
 * is a finished plan leaving value on the table, unplanned spots are just work
 * still to do.
 */
export function alertFor({ left, emptySlots, avg, targeted, cap, unplanned }) {
  // 🔴 THE TWO BRANCHES BELOW ASK "HOW MANY SPOTS STILL NEED MONEY", which is
  // not the same question as "how many are open" once PER SPOT exists: a spot
  // with a $40 rail and no name is open, but its money is already set aside and
  // counted in PLANNED, so testing `left` against it double counts it and
  // reports a fully budgeted plan as one that cannot fill its roster. In total
  // mode an earmarked spot is not open at all, so `unplanned` equals
  // `emptySlots` there and neither branch moves.
  const needsMoney = unplanned == null ? emptySlots : unplanned;
  if (left < 0) {
    return { tone: theme.colors.status.danger, text: `${money(-left)} over budget — trim a primary or promote a cheaper backup.` };
  }
  // Earmarks can fit while the NAMES do not: raising a primary's price lifts
  // `targeted` but not `planned`, so `left` stays >= 0 and the plan reads clean
  // at $205 against a $200 cap. The gap between planned and targeted is normally
  // the useful signal this design intends — it is only a problem once it crosses
  // the cap, which is what this branch and nothing else catches.
  //
  // It sits AFTER the left < 0 branch on purpose: a genuine earmark overrun is
  // the more direct statement of the same trouble and should keep the message.
  if (cap > 0 && targeted > cap) {
    return {
      tone: theme.colors.status.danger,
      text: `${money(targeted)} of names against a ${money(cap)} budget — your earmarks fit but the players you listed cost ${money(targeted - cap)} more than you have.`,
    };
  }
  if (needsMoney > 0 && left < needsMoney) {
    return { tone: theme.colors.status.danger, text: `Only ${money(left)} left for ${needsMoney} open ${needsMoney === 1 ? "spot" : "spots"} — this plan cannot fill the roster.` };
  }
  if (emptySlots === 0 && left > 0) {
    return { tone: theme.colors.status.success, text: `Roster fully planned with ${money(left)} of dead money — that is budget you could still put to work.` };
  }
  if (needsMoney > 0) {
    return { tone: theme.colors.status.warning, text: `${needsMoney} ${needsMoney === 1 ? "spot" : "spots"} unplanned, about ${money(avg)} each.` };
  }
  return null;
}

export default function PlanSummary({
  cap, planned, targeted, left, avg, maxBid, filled, committed, total, emptySlots, byPos,
  onCapChange, startersPpg, startersUnprojected = 0,
  secured = 0, lockedSlots = 0, unplanned, budgetMode = "total",
}) {
  const bySlot = budgetMode === "slot";
  // 🔴 THE ONLY BRANCH IN THIS FILE, and everything it gates is layout. No
  // figure, no threshold and no string in the alert changes with it; a phone
  // reads the same plan the desktop does, arranged to fit.
  const phone = useIsNarrow(PHONE_BREAKPOINT);
  const [capFocused, setCapFocused] = useState(false);
  const segments = Object.entries(byPos || {})
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1]);

  // Segments are sized against the CAP, not against PLANNED, so the bar reads
  // as "how much of the budget is committed" and the remainder is real. An
  // over-budget plan overflows the track rather than silently rescaling to fit.
  const denom = cap > 0 ? cap : Math.max(planned, 1);
  const gap = Math.round(planned - targeted);
  // The partition. `open` is what TARGETED shows once anything is locked; with
  // nothing locked it IS targeted, which is why the tile below can read from
  // one expression in both cases.
  const hasLocks = secured > 0;
  const open = targeted - secured;
  const openSpots = Math.max(0, filled - lockedSlots);
  const alert = alertFor({ left, emptySlots, avg, targeted, cap, unplanned });

  // Open once and it stays open for the session. Never persisted.
  const [expanded, setExpanded] = useState(false);

  /**
   * The four figures the collapsed line carries, and they are the four you read
   * out loud at a table: what the budget is, what the plan scores, what is left
   * and what you could bid right now. PLANNED, TARGETED and SECURED are working
   * numbers you check while editing, so they stay behind the chevron.
   *
   * A dash where a figure has nothing to say, on the same rules the expanded
   * block uses: no PPG until a primary is projected, no max bid on a full
   * roster. "$0" would be a claim; a dash is the absence of one.
   */
  const oneLine = [
    `${cap > 0 ? money(cap) : "\u2014"} budget`,
    `${startersPpg > 0 || startersUnprojected > 0 ? formatPpgTotal(startersPpg, startersUnprojected) : "\u2014"} PPG`,
    `${money(left)} left`,
    `max bid ${emptySlots === 0 ? "\u2014" : money(maxBid)}`,
  ].join(" \u00B7 ");

  return (
    <div style={{
      padding: theme.spacing.xl,
      borderRadius: theme.radius.xl,
      background: theme.colors.bg.surface,
      border: `1px solid ${theme.colors.border.subtle}`,
    }}>
      {/* 🔴 COLLAPSED BY DEFAULT ON A PHONE, and this is about what is
          BELOW it rather than about the summary itself. Expanded, this block is
          roughly two thirds of a 412px screen, so the matrix — the thing the
          page is for — opened below the fold every single time. One line of the
          four figures you actually read at a table, and the rest a tap away.

          Session state, deliberately not on the plan: it is a view, it does not
          survive a reload, and putting it on the plan would drag it into the
          optimizer's fingerprint and mark a plan stale for being read. */}
      {phone && (
        <button
          type="button"
          data-testid="summary-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: "flex", alignItems: "center", gap: theme.spacing.sm,
            width: "100%",
            padding: 0,
            border: "none",
            background: "transparent",
            color: theme.colors.text.secondary,
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSize.small,
            fontWeight: theme.fontWeight.medium,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span data-testid="summary-oneline" style={{
            flex: 1, minWidth: 0,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{oneLine}</span>
          <span aria-hidden="true" style={{ flexShrink: 0, color: theme.colors.text.faint }}>
            {expanded ? "\u25BE" : "\u25B8"}
          </span>
        </button>
      )}

      {(!phone || expanded) && (
        <>
        {/* 🔴 A COLUMN ON A PHONE, and the two blocks then take the full width
            each rather than fighting over one row. On desktop this is the same
            space-between row it has always been. */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: phone ? theme.spacing.lg : theme.spacing.xl, flexWrap: "wrap",
          flexDirection: phone ? "column" : "row",
        }}>
          <div style={phone ? { width: "100%" } : undefined}>
          {/* 🔴 TWO HEROES, AND THE SECOND ONE IS THE POINT OF THE PAGE. What a
               plan is FOR is the points it scores; the budget is the constraint
               it scores them under. STARTERS PPG spent this feature's life as the
               sixth tile in a right-hand row of six, at tile size, after MAX BID,
               which said the opposite. It is the same number and the same
               sub-label, moved to where the answer belongs. */}
          {/* 🔴 SIDE BY SIDE ON A PHONE TOO — nowrap, not a narrower wrap. The
              two heroes are the answer to "what is my budget" and "what does this
              plan score", and stacking them pushes the second below the fold on
              the one screen size where the fold is closest. They fit: $200 and a
              three-digit PPG at 26px is about 170px of ink in a 340px row. */}
          <div style={{
            display: "flex", alignItems: "flex-start",
            gap: phone ? theme.spacing.lg : theme.spacing.xxl,
            flexWrap: phone ? "nowrap" : "wrap",
            justifyContent: phone ? "space-between" : undefined,
          }}>
            <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.heavy,
              letterSpacing: 0.8,
              color: theme.colors.text.faint,
              textTransform: "uppercase",
            }}>Total budget</div>
            {/* The hero is EDITABLE — a league budget is a setting you change,
                not a fact you read, and setPlanBudget existed with nothing
                calling it. Styled to stay a hero rather than become a form
                field: no box, transparent background, display face at hero size,
                with only a faint dashed underline hinting it is typeable until
                focus turns it orange. Same rules as every other input here —
                writes straight through, select-on-focus, no local draft state. */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <span style={{
                fontFamily: theme.fonts.display,
                fontSize: theme.fontSize.hero,
                fontWeight: theme.fontWeight.black,
                color: capFocused ? theme.colors.brand.orange : theme.colors.text.primary,
                lineHeight: "30px",
              }}>$</span>
              <input
                type="number"
                min={0}
                value={cap}
                onFocus={(e) => { e.target.select(); setCapFocused(true); }}
                onBlur={() => setCapFocused(false)}
                onChange={(e) => onCapChange && onCapChange(e.target.value)}
                title="Your league's auction budget"
                style={{
                  width: phone ? 72 : 96,
                  padding: 0,
                  border: "none",
                  borderBottom: `1px ${capFocused ? "solid" : "dashed"} ${capFocused ? theme.colors.brand.orange : theme.colors.border.default}`,
                  background: "transparent",
                  outline: "none",
                  fontFamily: theme.fonts.display,
                  fontSize: theme.fontSize.hero,
                  fontWeight: theme.fontWeight.black,
                  color: capFocused ? theme.colors.brand.orange : theme.colors.text.primary,
                  lineHeight: "30px",
                }}
              />
            </div>
            {/* The one line this hero has never carried. It exists only on the
                phone, where STARTERS PPG beside it has a sub and a bare number
                next to a captioned one reads as unfinished. Same words as the
                input's own tooltip. */}
            {phone && (
              <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, marginTop: 1 }}>
                auction budget
              </div>
            )}
            </div>

            {/* 🔴 STARTERS ONLY, and the sub-label says so on screen rather than
                leaving the scope in somebody's head. A bench player scores you
                nothing, so folding the bench in would produce a number that looks
                like a lineup total and is not one.

                🔴 The unprojected qualifier is not decoration either. An
                unresolvable primary scores 0, so without it this reads as a
                complete lineup total while silently omitting a player. */}
            <div title={startersUnprojected > 0
              ? `Mock1's projected points per game, summed across your primary target at every non-bench slot. ${startersUnprojected} of them has no projection and contributes nothing to this total, so the number is short by whatever they are worth.`
              : "Mock1's projected points per game, season projection over 17, summed across your primary target at every non-bench slot."}>
              <div style={{
                fontSize: theme.fontSize.caption,
                fontWeight: theme.fontWeight.heavy,
                letterSpacing: 0.8,
                color: theme.colors.text.faint,
                textTransform: "uppercase",
              }}>Starters PPG</div>
              <div style={{
                fontFamily: theme.fonts.display,
                fontSize: theme.fontSize.hero,
                fontWeight: theme.fontWeight.black,
                color: theme.colors.text.primary,
                lineHeight: "30px",
              }}>
                {startersPpg > 0 || startersUnprojected > 0
                  ? formatPpgTotal(startersPpg, startersUnprojected)
                  : "—"}
              </div>
              {/* Same fact, short enough to stay on one line at 412px. The full
                  sentence is in the tooltip on the block above either way, so
                  nothing is only readable on a desktop. */}
              <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, marginTop: 1 }}>
                {phone
                  ? (startersUnprojected > 0 ? `starters only · ${startersUnprojected} no proj` : "starters only")
                  : (startersUnprojected > 0
                    ? `primary targets, bench excluded · ${startersUnprojected} with no projection`
                    : "primary targets, bench excluded")}
              </div>
            </div>
          </div>

            <div style={{ fontSize: theme.fontSize.small, color: theme.colors.text.muted, marginTop: 2 }}>
              {committed} of {total} spots committed · {filled} with a name
            </div>
          </div>

          {/* AVG PER OPEN is deliberately gone: the even-split number already
              appears, in words, in the warning banner below. */}
          {/* Five tiles now, since STARTERS PPG went to the left as a hero.
              flexWrap lets them fall to a second row when the width runs out —
              deliberately, rather than shrinking the type, which was just
              deliberately increased. justifyContent flex-end keeps the wrapped
              row aligned under the first. */}
          {/* 🔴 A 2x2 GRID ON A PHONE, NOT A WRAPPED ROW. Right-aligned tiles
              that wrap put PLANNED and TARGETED on one line and LEFT and MAX BID
              on the next with a ragged left edge and no shared column; an
              explicit two-track grid gives four equal boxes you can compare down
              as well as across. SECURED, when it exists, simply takes the fifth
              cell and the pair below it moves down a row. */}
          <div style={phone ? {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: theme.spacing.md,
            width: "100%",
          } : { display: "flex", gap: theme.spacing.xxl, flexWrap: "wrap", justifyContent: "flex-end", rowGap: theme.spacing.lg }}>
            <Stat
              compact={phone}
              label="Planned"
              value={money(planned)}
              sub="set aside"
              title="The sum of your per-slot earmarks. A slot with no earmark counts its primary's price."
            />
            {/* 🔴 FIRST OF THE TWO, because it is the one you cannot change. It
                sits immediately left of TARGETED so the pair reads as a split of
                one number rather than as two unrelated tiles, and it is suppressed
                entirely at zero — a planner who never locks anything must not pay
                a tile's worth of width for a feature they are not using. */}
            {hasLocks && (
              <Stat
                compact={phone}
                label="Secured"
                value={money(secured)}
                sub={`${lockedSlots} locked`}
                accent={theme.colors.lock}
                title="What the players you have already won cost you. Locked slots are held by the optimizer and their money comes off the budget before it solves anything."
              />
            )}
            <Stat
              compact={phone}
              label="Targeted"
              value={money(hasLocks ? open : targeted)}
              sub={hasLocks
                ? `still to buy · ${openSpots} ${openSpots === 1 ? "name" : "names"}`
                : (gap === 0 ? "what your picks cost" : `${money(Math.abs(gap))} ${gap > 0 ? "of room" : "over your earmarks"}`)}
              accent={!hasLocks && gap < 0 ? theme.colors.status.danger : undefined}
              title={hasLocks
                ? `What the primaries you have NOT locked would cost. With SECURED it sums to ${money(targeted)}, the whole of what your listed names cost.`
                : "What the primaries you listed would actually cost. The gap against PLANNED is the room you left yourself."}
            />
            <Stat compact={phone} label="Left" value={money(left)} accent={left < 0 ? theme.colors.status.danger : undefined}
              title="Total budget minus PLANNED." />
            {/* 🔴 Earmarking an empty slot moves max bid by (earmark − 1), not by
                the earmark, because that slot stops demanding its own $1
                holdback. Correct, but it reads as an off-by-one unless something
                says so — hence both the sub-label and the tooltip. */}
            {/* A full roster has no next player to bid on, so maxBid is
                legitimately 0 — but "$0" under a sub-label about unplanned spots
                explains nothing and reads like a bug. The arithmetic is
                untouched; only the presentation says what the 0 means. */}
            <Stat
              compact={phone}
              label="Max bid"
              value={emptySlots === 0 ? "—" : money(maxBid)}
              accent={theme.colors.brand.orange}
              sub={emptySlots === 0
                ? "roster is full"
                : (bySlot
                  ? "leaves every other open spot its own budget"
                  : "leaves your earmarks and $1 for every unplanned spot")}
              title={bySlot
                // 🔴 PER SPOT SAYS SOMETHING DIFFERENT, because a budgeted spot
                // with no name is still open here and still has to be filled. So
                // the holdback is that spot's own budget rather than a dollar,
                // and the biggest bid you can make is the one that frees your
                // largest rail.
                ? `$${Math.round(cap)} budget, minus what every OTHER open spot is holding: its own budget where it has one, $1 where it does not. With your per spot budgets in use, a budgeted spot with no player is still open, so this is the most you could pay for one player right now.`
                : `$${Math.round(left)} left, minus $1 for each of the ${emptySlots} slot${emptySlots === 1 ? "" : "s"} with neither a budget nor a player. Earmarked slots are already paid for out of PLANNED, so they no longer hold back a dollar.`}
            />
          </div>
        </div>

        {/* Stacked allocation bar, following PLANNED — the segments come from
            spendingByPos, which the hook buckets off slotBudget, so the legend
            sums to PLANNED and not to TARGETED. 2px gap between segments; the
            unspent remainder is bg.raised so it reads as track, not a position. */}
        <div style={{
          display: "flex", gap: 2,
          height: 10,
          marginTop: theme.spacing.lg,
          borderRadius: theme.radius.sm,
          overflow: "hidden",
          background: theme.colors.bg.raised,
        }}>
          {segments.map(([pos, amount]) => (
            <div key={pos} title={`${pos} ${money(amount)}`} style={{
              width: `${(amount / denom) * 100}%`,
              background: theme.colors.pos[pos] || theme.colors.text.faint,
            }} />
          ))}
          {left > 0 && <div style={{ flex: 1, background: theme.colors.bg.raised }} />}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: theme.spacing.lg, marginTop: theme.spacing.md }}>
          {segments.map(([pos, amount]) => (
            <span key={pos} style={{ display: "flex", alignItems: "center", gap: theme.spacing.xs }}>
              <span style={{
                width: 8, height: 8, borderRadius: 2,
                background: theme.colors.pos[pos] || theme.colors.text.faint,
              }} />
              {/* Direct label. The swatch is a convenience, the words are the
                  legend — never the other way round. */}
              <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.secondary, fontWeight: theme.fontWeight.medium }}>
                {pos} {money(amount)}
              </span>
            </span>
          ))}
          {left > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: theme.spacing.xs }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}` }} />
              <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint, fontWeight: theme.fontWeight.medium }}>
                UNSPENT {money(left)}
              </span>
            </span>
          )}
        </div>
        </>
      )}

      {alert && (
        <div style={{
          marginTop: theme.spacing.lg,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          borderLeft: `3px solid ${alert.tone}`,
          background: theme.colors.bg.deep,
          color: alert.tone,
          fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.medium,
        }}>
          {alert.text}
        </div>
      )}
    </div>
  );
}
